import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

function getSessionId(): string {
  const key = "lp_session_id";
  let sid = sessionStorage.getItem(key);
  if (!sid) {
    sid = crypto.randomUUID();
    sessionStorage.setItem(key, sid);
  }
  return sid;
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

const PageVisitTracker = () => {
  const location = useLocation();
  const { user } = useAuth();
  const lastPath = useRef<string>("");
  const lastUserForPath = useRef<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith("/admin") || path.startsWith("/worker")) return;

    const userChanged = path === lastPath.current && user?.id && lastUserForPath.current !== user.id;
    if (path === lastPath.current && !userChanged) return;

    lastPath.current = path;
    lastUserForPath.current = user?.id || null;

    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(async () => {
      try {
        await fetch(`${SUPABASE_URL}/functions/v1/track-page-visit`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "apikey": SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            session_id: getSessionId(),
            page_path: path,
            referrer: document.referrer || null,
            user_agent: navigator.userAgent || null,
            user_id: user?.id || null,
          }),
        });
      } catch (err) {
        console.warn("Page visit tracking failed:", err);
      }
    }, 500);

    return () => clearTimeout(timeoutRef.current);
  }, [location.pathname, user?.id]);

  return null;
};

export default PageVisitTracker;
